<template>
  <div class="space-y-4">
    <div class="flex flex-wrap items-center gap-3 justify-between">
      <div class="flex items-center gap-2 flex-wrap">
        <button class="btn-secondary px-2" type="button" title="Back to vendors" @click="router.back()">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>

        <input v-model="query" class="input w-72" placeholder="Search Odoo vendors by name…" type="search" autofocus />

        <select v-model.number="limit" class="select w-28" @change="reload">
          <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }} / page</option>
        </select>
      </div>

      <button class="btn-primary" type="button" :disabled="loading" @click="reload">
        {{ loading ? 'Loading…' : 'Refresh' }}
      </button>
    </div>

    <div v-if="error" class="card border-red-200 bg-red-50">
      <p class="text-sm font-semibold text-red-700 m-0">Failed to load vendors from Odoo</p>
      <p class="text-xs text-red-700 mt-1 m-0">{{ error }}</p>
    </div>

    <div v-else-if="loading && !hasLoaded" class="card">
      <p class="text-sm text-gray-500 m-0">Loading vendors from Odoo…</p>
    </div>

    <template v-else-if="hasLoaded">
      <div class="card p-0 overflow-hidden">
        <div v-if="filteredVendors.length === 0" class="p-6">
          <p class="text-sm text-gray-500 m-0">No Odoo vendors match this search.</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="table-th w-16">Id</th>
                <th class="table-th min-w-[220px]">Name</th>
                <th class="table-th min-w-[180px]">Email</th>
                <th class="table-th">Phone</th>
                <th class="table-th">City</th>
                <th class="table-th">Country</th>
                <th class="table-th w-28 text-center">Supplier rank</th>
                <th class="table-th w-24">Active</th>
                <th class="table-th w-28 text-right">Action</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 bg-white">
              <tr v-for="vendor in filteredVendors" :key="vendor.id" class="hover:bg-blue-50 transition-colors">
                <td class="table-td text-gray-500 whitespace-nowrap">{{ vendor.id }}</td>
                <td class="table-td font-medium text-gray-900 max-w-xs">
                  <div class="truncate" :title="vendor.displayName">{{ vendor.displayName }}</div>
                </td>
                <td class="table-td max-w-[220px]">
                  <div class="truncate" :title="vendor.email ?? undefined">{{ vendor.email ?? '—' }}</div>
                </td>
                <td class="table-td whitespace-nowrap">{{ vendor.phone ?? '—' }}</td>
                <td class="table-td whitespace-nowrap">{{ vendor.city ?? '—' }}</td>
                <td class="table-td whitespace-nowrap">{{ vendor.country?.name ?? '—' }}</td>
                <td class="table-td text-center whitespace-nowrap">{{ vendor.supplierRank }}</td>
                <td class="table-td whitespace-nowrap">
                  <span class="badge" :class="vendor.active ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'">
                    {{ vendor.active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="table-td text-right whitespace-nowrap">
                  <router-link
                    v-if="archivedVendors.get(vendor.id)"
                    :to="{ name: 'VendorDetail', params: { id: archivedVendors.get(vendor.id)!.id } }"
                    class="badge bg-amber-50 text-amber-800 hover:underline"
                    title="Already in the portal, archived — open it to unarchive"
                    aria-label="Archived — open the vendor to unarchive it"
                  >
                    Archived
                  </router-link>
                  <span v-else-if="vendorStore.isAdded(vendor.id)" class="badge bg-gray-100 text-gray-500">Added</span>
                  <button v-else class="btn-primary" type="button" @click="selectVendor(vendor)">Add</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-3">
        <p class="text-sm text-gray-500 m-0">Showing {{ vendors.length }} of {{ total }} matching vendor(s) in Odoo — offset {{ offset }}</p>

        <div class="flex items-center gap-3">
          <button class="btn-secondary" type="button" :disabled="loading || offset === 0" @click="goToPreviousPage">Previous</button>
          <span class="text-sm text-gray-500 whitespace-nowrap">Page {{ currentPage }} of {{ totalPages }}</span>
          <button class="btn-secondary" type="button" :disabled="loading || !hasNextPage" @click="goToNextPage">Next</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { fetchOdooVendors } from '@/services/odooVendorService';
  import { useVendorStore } from '@/store/vendorStore';
  import type { OdooVendor } from '@/types/odooVendor';
  import type { Vendor } from '@/types/vendor';
  import { isArchived } from '@/utils/vendorArchive';
  import { extractErrorMessage } from '@/utils/apiError';

  const pageSizeOptions = [10, 20, 50, 100];
  const defaultLimit = 20;
  const defaultOffset = 0;
  const searchDebounceMs = 300;

  const router = useRouter();
  const vendorStore = useVendorStore();

  const vendors = ref<OdooVendor[]>([]);
  const total = ref(0);
  const limit = ref(defaultLimit);
  const offset = ref(defaultOffset);
  const loading = ref(false);
  const hasLoaded = ref(false);
  const error = ref<string | null>(null);
  const query = ref('');
  let searchDebounce: ReturnType<typeof setTimeout> | null = null;

  const archivedVendors = computed(
    () => new Map<number, Vendor>(vendorStore.vendors.filter(isArchived).map((v) => [v.odooVendorId, v])),
  );
  // The search is server-side (Odoo `name ilike`), so results span all pages, not just the loaded one.
  const filteredVendors = computed(() => vendors.value);
  const currentPage = computed(() => Math.floor(offset.value / limit.value) + 1);
  const totalPages = computed(() => Math.max(1, Math.ceil(total.value / limit.value)));
  const hasNextPage = computed(() => offset.value + limit.value < total.value);

  async function load() {
    loading.value = true;
    error.value = null;

    try {
      const search = query.value.trim();
      const result = await fetchOdooVendors(limit.value, offset.value, search === '' ? undefined : search);
      vendors.value = result.data;
      total.value = result.total;
    } catch (loadError) {
      error.value = extractErrorMessage(loadError);
    } finally {
      loading.value = false;
      hasLoaded.value = true;
    }
  }

  watch(query, () => {
    if (searchDebounce) clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => {
      offset.value = defaultOffset;
      void load();
    }, searchDebounceMs);
  });

  onBeforeUnmount(() => {
    if (searchDebounce) clearTimeout(searchDebounce);
  });


  function reload() {
    offset.value = defaultOffset;
    void load();
  }

  function goToPreviousPage() {
    offset.value = Math.max(defaultOffset, offset.value - limit.value);
    void load();
  }

  function goToNextPage() {
    offset.value += limit.value;
    void load();
  }

  function selectVendor(vendor: OdooVendor) {
    void router.push({ name: 'VendorImport', params: { odooId: vendor.id } });
  }

  onMounted(() => {
    // Populate the "Added" badges regardless of whether the user arrived via
    // VendorList.vue or navigated here directly (bookmark/deep link/refresh).
    // vendorStore.fetchVendors() catches its own errors internally, so a failed
    // backend call here just leaves badges stale — it must not block the Odoo
    // vendor browse table below, which is this screen's primary function.
    void vendorStore.fetchVendors();
    void load();
  });
</script>
